<template>
	<v-dialog
		v-model="show"
		max-width="500px"
		:scrim="false"
		:location="dialogPosition"
		transition="slide-y-reverse-transition"
	>
		<v-card class="extraction-history-card d-flex flex-column">
			<v-toolbar color="primary" dark flat dense>
				<v-toolbar-title class="text-subtitle-1">
					Extraction History
				</v-toolbar-title>
				<v-spacer />
				<v-btn icon size="small" @click="close">
					<v-icon>mdi-close</v-icon>
				</v-btn>
			</v-toolbar>

			<v-card-text v-if="loading" class="pa-4 text-center">
				<v-progress-circular indeterminate color="primary" />
			</v-card-text>

			<v-card-text
				v-else-if="groupedEvents.length === 0"
				class="pa-4 text-center text-body-2 text-fg-2"
			>
				No history available
			</v-card-text>

			<v-list v-else density="compact" class="py-2">
				<template
					v-for="(group, index) in groupedEvents"
					:key="group.timestamp"
				>
					<v-divider v-if="index > 0" />
					<v-list-subheader class="text-caption font-weight-bold">
						{{ formatTimestamp(group.timestamp) }}
					</v-list-subheader>
					<v-list-item
						v-for="event in group.events"
						:key="event.id"
						class="px-4 event-list-item"
					>
						<template #prepend>
							<v-icon
								:color="getEventColor(event.event_type || event.eventType)"
								size="small"
								class="mr-2"
							>
								{{ getEventIcon(event.event_type || event.eventType) }}
							</v-icon>
						</template>
						<v-list-item-title class="text-body-2 text-wrap">
							{{ getEventDescription(event) }}
						</v-list-item-title>
						<v-list-item-subtitle class="text-caption">
							by
							{{ (event.event_user || event.eventUser)?.email || "Unknown" }}
						</v-list-item-subtitle>
					</v-list-item>
				</template>
			</v-list>
		</v-card>
	</v-dialog>
</template>

<script>
import { useMainStore } from "@/store";
import { WORKFLOW_STATES } from "@/constants/workflowStates";

export default {
	name: "ExtractionHistoryModal",

	props: {
		dataEntryUuid: {
			type: String,
			default: null,
		},
	},

	emits: ["close"],

	data() {
		return {
			show: false,
			loading: false,
			events: [],
			dialogPosition: "bottom end",
		};
	},

	computed: {
		groupedEvents() {
			// Group events by timestamp (rounded to the second)
			const groups = {};

			this.events.forEach((event) => {
				// Handle both created_at (snake_case) and createdAt (camelCase)
				const dateValue = event.event_timestamp || event.created_at;

				if (!dateValue) {
					console.warn("Event missing timestamp:", event);
					return;
				}

				const date = new Date(dateValue);

				// Validate date
				if (isNaN(date.getTime())) {
					console.warn("Invalid date for event:", event, dateValue);
					return;
				}

				const timestamp = date.toISOString();
				if (!groups[timestamp]) {
					groups[timestamp] = {
						timestamp,
						events: [],
					};
				}
				groups[timestamp].events.push(event);
			});

			// Convert to array and sort by timestamp descending
			return Object.values(groups).sort(
				(a, b) =>
					new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
			);
		},
	},

	methods: {
		async fetchEvents() {
			if (!this.dataEntryUuid) return;

			this.loading = true;
			try {
				const response = await this.$http.get(
					`/data-entry/${this.dataEntryUuid}/extraction-events`,
				);
				this.events = response.data.events || [];
			} catch (error) {
				console.error("Error fetching extraction events:", error);
				useMainStore().showNotification({
					message: "Failed to load extraction history",
					status: "error",
				});
				this.events = [];
			} finally {
				this.loading = false;
			}
		},

		open() {
			this.show = true;
			if (this.dataEntryUuid) {
				this.fetchEvents();
			}
		},

		close() {
			this.show = false;
			this.$emit("close");
		},

		formatTimestamp(timestamp) {
			return new Date(timestamp).toLocaleString();
		},

		// DCTL-2294: "reopened" is an extraction sent back to the curator - the
		// rework state under another name, and the last of the amber-as-rework
		// sites. Glyph and colour come from the shared map. mdi-lock-open described
		// the mechanism (the record is editable again), not what the reader needs to
		// know, which is that the work came back.
		getEventIcon(eventType) {
			const icons = {
				created: "mdi-file-plus",
				reassigned: "mdi-account-arrow-right",
				completed: "mdi-check-circle",
				reopened: WORKFLOW_STATES.rework.icon,
				// DCTL-2429: the extraction was discarded to release a stale claim.
				discarded: "mdi-delete-outline",
				// DCTL-2562: a recovered document was promoted back over the
				// stored SDE after the curator's saves were rejected.
				restored: "mdi-backup-restore",
				// DCTL-XXXX (DHELP-437): retired by "Restart extraction".
				superseded: "mdi-restart",
			};
			return icons[eventType] || "mdi-circle";
		},

		// Feeds `<v-icon :color>`, a FOREGROUND use, so rework takes the -ink token
		// rather than the base fill value.
		getEventColor(eventType) {
			const colors = {
				created: "blue-grey",
				reassigned: "info",
				completed: "success",
				reopened: WORKFLOW_STATES.rework.ink,
				discarded: "error",
				restored: "info",
				superseded: "grey-darken-2",
			};
			return colors[eventType] || "grey-darken-2";
		},

		getEventDescription(event) {
			const eventType = event.event_type || event.eventType;

			switch (eventType) {
				case "created":
					return "Extraction created";
				case "reassigned": {
					const fromUser = event.from_user || event.fromUser;
					const toUser = event.to_user || event.toUser;
					return `Reassigned from ${fromUser?.email || "Unknown"} to ${
						toUser?.email || "Unknown"
					}`;
				}
				case "completed":
					return "Extraction completed";
				case "reopened":
					return "Extraction reopened";
				// DCTL-2429: a manager discarded this extraction to release the
				// assignment. from_user is the curator whose work was discarded.
				case "discarded": {
					const owner = event.from_user || event.fromUser;
					return `Extraction discarded to release ${
						owner?.email || "the assignee"
					}'s claim`;
				}
				// DCTL-2562: a recovered document was promoted over the stored SDE.
				// The description the command wrote names the source snapshots and
				// the point counts, so show it rather than a generic label.
				case "restored":
					return event.description || "Extraction data restored";
				// DCTL-XXXX (DHELP-437): a manager restarted the paper's
				// extraction from scratch, retiring this one. It stays viewable.
				case "superseded":
					return "Extraction retired when the paper was restarted from scratch";
				default:
					return eventType || "Unknown event";
			}
		},
	},
};
</script>

<style scoped>
.extraction-history-card {
	max-height: 500px;
}

.extraction-history-card .v-list {
	overflow-y: auto;
}

.event-list-item :deep(.v-list-item__content) {
	overflow: visible;
	white-space: normal;
}

.event-list-item :deep(.v-list-item-title) {
	white-space: normal !important;
	overflow-wrap: break-word;
	word-break: break-word;
}

:deep(.v-overlay__content) {
	margin: 16px;
}
</style>
